/**
 * usePatientReportWizard — state for the patient-reports one-pager builder.
 *
 * No step navigation: all sections are visible at once (2026 design language —
 * free navigation, no step wizards). This hook holds the selections and the
 * per-section validity gates; the preview/export section activates once
 * everything required is selected.
 *
 * All report-type-specific behavior (columns, row building) comes from
 * reportTypeRegistry — this hook is generic.
 */

import { useCallback, useMemo, useState } from "react";
import reportTypeRegistry, {
  getReportType,
  getVisibleColumns,
} from "./reportTypeRegistry";
import {
  FIN_DEFAULT_STATUSES,
  FIN_CHART_OPTIONS,
  FIN_SECTION_OPTIONS,
} from "./finance/financeReportModel";

const defaultFinanceOptions = () => ({
  statuses: new Set(FIN_DEFAULT_STATUSES),
  charts: Object.fromEntries(FIN_CHART_OPTIONS.map((c) => [c.key, c.defaultOn !== false])),
  sections: Object.fromEntries(FIN_SECTION_OPTIONS.map((s) => [s.key, s.defaultOn !== false])),
});

const pad2 = (n) => String(n).padStart(2, "0");

const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
};

/**
 * First/last day of a month as ISO yyyy-mm-dd strings.
 * The CURRENT month is "up to date": its end is today, not the month's end.
 */
export const getMonthBounds = (year, month) => {
  const now = new Date();
  const isCurrentMonth =
    year === now.getFullYear() && month === now.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();
  return {
    start: `${year}-${pad2(month)}-01`,
    end: isCurrentMonth
      ? todayIso()
      : `${year}-${pad2(month)}-${pad2(lastDay)}`,
  };
};

const defaultColumnsFor = (reportTypeId, canAccessFinance) => {
  const type = getReportType(reportTypeId);
  const enabled = {};
  getVisibleColumns(type, canAccessFinance).forEach((c) => {
    enabled[c.key] = c.defaultOn !== false;
  });
  return enabled;
};

// Smart default: current month pre-selected so a report is one patient-pick
// away; the user can still change the range freely.
const getCurrentMonthDefault = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  return { year, month, ...getMonthBounds(year, month) };
};

function usePatientReportWizard({ canAccessFinance }) {
  const [patientId, setPatientId] = useState(null);
  const [reportTypeId, setReportTypeId] = useState(
    reportTypeRegistry[0]?.id || null
  );
  const [initialMonth] = useState(getCurrentMonthDefault);
  const [startDate, setStartDate] = useState(initialMonth.start);
  const [endDate, setEndDate] = useState(initialMonth.end);
  // Quick single-month preset — {year, month} while the range matches a whole
  // month, null once the user edits dates manually.
  const [presetMonth, setPresetMonth] = useState({
    year: initialMonth.year,
    month: initialMonth.month,
  });
  const [enabledColumns, setEnabledColumns] = useState(() =>
    defaultColumnsFor(reportTypeRegistry[0]?.id, canAccessFinance)
  );

  // ─── Finance report flow state (flow: "finance") ───
  const [financeOptions, setFinanceOptions] = useState(defaultFinanceOptions);

  const reportType = useMemo(() => getReportType(reportTypeId), [reportTypeId]);
  const visibleColumns = useMemo(
    () => getVisibleColumns(reportType, canAccessFinance),
    [reportType, canAccessFinance]
  );

  // Flow-specific options reset when the type or patient changes.
  const resetFlowOptions = useCallback(() => {
    setFinanceOptions(defaultFinanceOptions());
  }, []);

  const toggleFinanceStatus = useCallback((key) => {
    setFinanceOptions((prev) => {
      const next = new Set(prev.statuses);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, statuses: next };
    });
  }, []);
  const setAllFinanceStatuses = useCallback((all) => {
    setFinanceOptions((prev) => ({
      ...prev,
      statuses: new Set(all ? FIN_DEFAULT_STATUSES.concat("cancelled") : []),
    }));
  }, []);
  const toggleFinanceChart = useCallback((key) => {
    setFinanceOptions((prev) => ({
      ...prev,
      charts: { ...prev.charts, [key]: !prev.charts[key] },
    }));
  }, []);
  const toggleFinanceSection = useCallback((key) => {
    setFinanceOptions((prev) => ({
      ...prev,
      sections: { ...prev.sections, [key]: !prev.sections[key] },
    }));
  }, []);

  const selectReportType = useCallback(
    (id) => {
      setReportTypeId(id);
      setEnabledColumns(defaultColumnsFor(id, canAccessFinance));
      resetFlowOptions();
    },
    [canAccessFinance, resetFlowOptions]
  );

  // Switching patient invalidates the treatment selection and any draft.
  const setPatientIdAndReset = useCallback(
    (id) => {
      setPatientId(id);
      resetFlowOptions();
    },
    [resetFlowOptions]
  );

  const toggleColumn = useCallback((key) => {
    setEnabledColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const applyMonthPreset = useCallback((year, month) => {
    const { start, end } = getMonthBounds(year, month);
    setPresetMonth({ year, month });
    setStartDate(start);
    setEndDate(end);
  }, []);

  const setStartDateManual = useCallback((iso) => {
    setPresetMonth(null);
    setStartDate(iso);
  }, []);

  const setEndDateManual = useCallback((iso) => {
    setPresetMonth(null);
    setEndDate(iso);
  }, []);

  // ─── Section validity gates ───
  // Clinic-scope reports have no patient step — the patient gate auto-passes.
  const needsPatient = reportType?.scope === "patient";
  const isFinanceFlow = reportType?.flow === "finance";
  const rangeInvalid = !!startDate && !!endDate && startDate > endDate;
  const patientOk = !needsPatient || !!patientId;
  const typeOk = !!reportTypeId;
  const rangeOk = !!startDate && !!endDate && !rangeInvalid;
  const columnsOk = visibleColumns.some((c) => enabledColumns[c.key]);
  // Table flows only — the finance flow has its own gates below.
  const isReady = !isFinanceFlow && patientOk && typeOk && rangeOk && columnsOk;
  const financeStatusesOk = financeOptions.statuses.size > 0;
  const financeContentOk =
    Object.values(financeOptions.charts).some(Boolean) ||
    Object.values(financeOptions.sections).some(Boolean);
  const financeReady = isFinanceFlow && rangeOk && financeStatusesOk && financeContentOk;

  const reset = useCallback(() => {
    setPatientId(null);
    resetFlowOptions();
    const def = getCurrentMonthDefault();
    setStartDate(def.start);
    setEndDate(def.end);
    setPresetMonth({ year: def.year, month: def.month });
    const firstId = reportTypeRegistry[0]?.id || null;
    setReportTypeId(firstId);
    setEnabledColumns(defaultColumnsFor(firstId, canAccessFinance));
  }, [canAccessFinance, resetFlowOptions]);

  return {
    reset,
    needsPatient,
    patientId,
    setPatientId: setPatientIdAndReset,
    // Finance report flow
    isFinanceFlow,
    financeOptions,
    toggleFinanceStatus,
    setAllFinanceStatuses,
    toggleFinanceChart,
    toggleFinanceSection,
    financeStatusesOk,
    financeContentOk,
    financeReady,
    reportTypeId,
    selectReportType,
    reportType,
    visibleColumns,
    startDate,
    endDate,
    setStartDateManual,
    setEndDateManual,
    presetMonth,
    applyMonthPreset,
    rangeInvalid,
    enabledColumns,
    toggleColumn,
    patientOk,
    typeOk,
    rangeOk,
    columnsOk,
    isReady,
  };
}

export default usePatientReportWizard;
